/**
 * RACE SUMMARY "Leg by leg" for one race, as a table in the report-table style: which half of the
 * course paid, the fleet's time in each half, and the gate each boat took into the leg with what it
 * gained (−) or lost (+) on the leader by the next mark. Data: race_timeline drivers.legs.
 */
import { For, Show } from "solid-js";
import SectionHead from "./SectionHead";
import { sourcesStore } from "../../../store/sourcesStore";
import type { RaceDriverGate, RaceDriverLeg } from "./raceDrivers";

/** A half "paid" when it closed on the leader at least this much faster, metres per minute. */
const PAID_M_PER_MIN = 25;

interface Props {
  legs: RaceDriverLeg[];
  sources: string[];
  /** Show only this leg's row (null = every leg). */
  leg?: number | null;
}

const metres = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))} m`;

export default function LegDriversPanel(props: Props) {
  const shown = (boat: string) => props.sources.length === 0 || props.sources.includes(boat);

  /** Which half paid on the leg (the row's tint): left = light red, right = light green, as the explore
   *  map's left / right of the mean wind; none = grey. */
  const side = (l: RaceDriverLeg): "left" | "right" | "none" => {
    const v = l.left_paid_m_per_min;
    if (v == null || Math.abs(v) < PAID_M_PER_MIN) {return "none";}
    return v > 0 ? "left" : "right";
  };

  const paid = (l: RaceDriverLeg) => {
    const v = l.left_paid_m_per_min;
    if (v == null) {return "—";}
    if (Math.abs(v) < PAID_M_PER_MIN) {return "Neither";}
    return `${v > 0 ? "Left" : "Right"} · ${Math.abs(Math.round(v))} m/min`;
  };

  const gateCell = (g: RaceDriverGate | undefined, last: boolean) => (
    <Show when={g} fallback={last ? "Finish" : "—"}>
      {(gate) => (
        <div class="race-legs-gate">
          <span class="race-legs-gate-mark">{gate().mark}</span>
          <For each={gate().boats.filter((b) => shown(b.boat))} fallback={<span class="race-legs-gate-boat">—</span>}>
            {(b) => (
              <span class="race-legs-gate-boat">
                <span class="race-drivers-dot" style={{ background: sourcesStore.getSourceColor(b.boat) || "var(--color-text-secondary)" }} />
                {b.boat}{" "}
                <span
                  class="race-drivers-num"
                  classList={{
                    "race-legs-gain": (b.dtl_change_m ?? 0) < 0,
                    "race-legs-loss": (b.dtl_change_m ?? 0) > 0,
                  }}
                >
                  {(b.dtl_change_m ?? 0) === 0 ? "led" : metres(b.dtl_change_m ?? 0)}
                </span>
              </span>
            )}
          </For>
        </div>
      )}
    </Show>
  );

  return (
    <section class="race-legs">
      <SectionHead title={props.leg != null ? `Leg ${props.leg}: side and gates` : "Leg by leg"} />
      <div class="data-table-container race-legs-table-container">
        <div class="scrollable-container">
          <table class="maneuvers-table race-legs-table">
            <thead>
              <tr>
                <th>LEG</th>
                <th>SIDE THAT PAID<br />(LOOKING UPWIND)</th>
                <th>FLEET LEFT / RIGHT<br />(LOOKING UPWIND)</th>
                <th>LEFT GATE</th>
                <th>RIGHT GATE</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.leg != null ? props.legs.filter((l) => l.leg === props.leg) : props.legs}>
                {(l) => (
                  <tr class={`race-legs-row-${side(l)}`}>
                    <td class="centered">{l.leg} {l.kind ?? (l.upwind ? "upwind" : "downwind")}</td>
                    <td class="centered">{paid(l)}</td>
                    <td class="centered race-drivers-num">
                      {l.left_share_pct != null ? `${l.left_share_pct}% / ${100 - l.left_share_pct}%` : "—"}
                    </td>
                    <td>{gateCell(l.gates?.find((g) => g.side === "left"), l.leg === props.legs.length)}</td>
                    <td>{gateCell(l.gates?.find((g) => g.side === "right"), l.leg === props.legs.length)}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </div>
      <p class="race-drivers-note">
        Left and right are as seen looking upwind on every leg, downwind legs included, so a side or gate means the same
        water on every leg. Side: which half of the course closed on the leader faster on straight-line sailing. Gates: the mark each boat
        rounded at the end of the leg, and what it gained (red, −) or lost (blue, +) on the leader from there to the next mark.
      </p>
    </section>
  );
}
